/**
 * Low-level E2E primitives: X25519 / Ed25519, X3DH, AES-GCM, HKDF.
 * Private keys never leave the client.
 */

import { ed25519, x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { b64ToBytes, bytesToB64, concatBytes, randomBytes, textEncoder } from './bytes';

export const E2E_VERSION = 1;
export const E2E_BODY_PREFIX = 'e2e1.';
export const OTP_BATCH_SIZE = 40;
export const MAX_SKIP = 200;

export const generateX25519Pair = () => {
  const secretKey = x25519.utils.randomPrivateKey();
  return { secretKey, publicKey: x25519.getPublicKey(secretKey) };
};

export const generateEd25519Pair = () => {
  const secretKey = ed25519.utils.randomPrivateKey();
  return { secretKey, publicKey: ed25519.getPublicKey(secretKey) };
};

export const dh = (secretKey, publicKey) => x25519.getSharedSecret(secretKey, publicKey);

export const signPrekey = (prekeyPublic, signingSecret) => (
  ed25519.sign(prekeyPublic, signingSecret)
);

export const verifySignedPrekey = (prekeyPublic, signature, signingPublic) => (
  ed25519.verify(signature, prekeyPublic, signingPublic)
);

export const kdf = (ikm, info, length = 32, salt = new Uint8Array(32)) => (
  hkdf(sha256, ikm, salt, textEncoder.encode(info), length)
);

export const kdfRk = (rootKey, dhOut) => {
  const okm = kdf(dhOut, 'LynxDRK', 64, rootKey);
  return {
    rootKey: okm.slice(0, 32),
    chainKey: okm.slice(32, 64),
  };
};

export const kdfCk = (chainKey) => {
  const messageKey = kdf(chainKey, 'LynxMsg', 32);
  const nextChain = kdf(chainKey, 'LynxChain', 32);
  return { messageKey, chainKey: nextChain };
};

/** Signal-style X3DH → 32-byte shared secret. */
export const x3dhInitiate = ({
  identitySecret,
  ephemeralSecret,
  peerIdentityPublic,
  peerSignedPrekeyPublic,
  peerOneTimePrekeyPublic = null,
}) => {
  const dh1 = dh(identitySecret, peerSignedPrekeyPublic);
  const dh2 = dh(ephemeralSecret, peerIdentityPublic);
  const dh3 = dh(ephemeralSecret, peerSignedPrekeyPublic);
  let ikm = concatBytes(dh1, dh2, dh3);
  if (peerOneTimePrekeyPublic) {
    ikm = concatBytes(ikm, dh(ephemeralSecret, peerOneTimePrekeyPublic));
  }
  return kdf(ikm, 'LynxX3DH', 32);
};

export const x3dhRespond = ({
  identitySecret,
  signedPrekeySecret,
  oneTimePrekeySecret = null,
  peerIdentityPublic,
  peerEphemeralPublic,
}) => {
  const dh1 = dh(signedPrekeySecret, peerIdentityPublic);
  const dh2 = dh(identitySecret, peerEphemeralPublic);
  const dh3 = dh(signedPrekeySecret, peerEphemeralPublic);
  let ikm = concatBytes(dh1, dh2, dh3);
  if (oneTimePrekeySecret) {
    ikm = concatBytes(ikm, dh(oneTimePrekeySecret, peerEphemeralPublic));
  }
  return kdf(ikm, 'LynxX3DH', 32);
};

export const aesGcmEncrypt = async (keyBytes, plaintextBytes, aadBytes = null) => {
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes instanceof Uint8Array ? keyBytes : new Uint8Array(keyBytes),
    { name: 'AES-GCM' },
    false,
    ['encrypt'],
  );
  const iv = randomBytes(12);
  // Chrome rejects `additionalData: undefined` ("Not a BufferSource").
  // Omit the field entirely when there is no AAD (favorites local seal).
  const params = { name: 'AES-GCM', iv };
  if (aadBytes != null && aadBytes.byteLength != null) {
    params.additionalData = aadBytes instanceof Uint8Array ? aadBytes : new Uint8Array(aadBytes);
  }
  const plain = plaintextBytes instanceof Uint8Array
    ? plaintextBytes
    : new Uint8Array(plaintextBytes);
  const cipherBuf = await crypto.subtle.encrypt(params, key, plain);
  return concatBytes(iv, new Uint8Array(cipherBuf));
};

export const aesGcmDecrypt = async (keyBytes, packed, aadBytes = null) => {
  const view = packed instanceof Uint8Array ? packed : new Uint8Array(packed);
  if (view.length < 13) {
    throw new Error('cipher too short');
  }
  const iv = view.slice(0, 12);
  const cipher = view.slice(12);
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes instanceof Uint8Array ? keyBytes : new Uint8Array(keyBytes),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );
  const params = { name: 'AES-GCM', iv };
  if (aadBytes != null && aadBytes.byteLength != null) {
    params.additionalData = aadBytes instanceof Uint8Array ? aadBytes : new Uint8Array(aadBytes);
  }
  const plainBuf = await crypto.subtle.decrypt(params, key, cipher);
  return new Uint8Array(plainBuf);
};

export const encodeWireBody = (packedCipher) => `${E2E_BODY_PREFIX}${bytesToB64(packedCipher)}`;

export const decodeWireBody = (body) => {
  const raw = String(body || '');
  if (!raw.startsWith(E2E_BODY_PREFIX)) {
    return null;
  }
  return b64ToBytes(raw.slice(E2E_BODY_PREFIX.length));
};

export const isE2EWireBody = (body) => String(body || '').startsWith(E2E_BODY_PREFIX);

export const fingerprintHex = (...publicKeys) => {
  const digest = sha256(concatBytes(...publicKeys.map((k) => (
    typeof k === 'string' ? b64ToBytes(k) : k
  ))));
  return Array.from(digest).map((b) => b.toString(16).padStart(2, '0')).join('');
};

/** Group fingerprint into readable safety-number blocks. */
export const formatSafetyNumber = (hex) => {
  const digits = hex.replace(/[^0-9a-f]/gi, '').toLowerCase();
  const blocks = [];
  for (let i = 0; i < digits.length; i += 5) {
    blocks.push(digits.slice(i, i + 5));
  }
  return blocks.join(' ').trim();
};

export const serializeKeyPair = (pair) => ({
  secretKey: bytesToB64(pair.secretKey),
  publicKey: bytesToB64(pair.publicKey),
});

export const deserializeKeyPair = (row) => ({
  secretKey: b64ToBytes(row.secretKey),
  publicKey: b64ToBytes(row.publicKey),
});

export const nextKeyId = (current = 0) => {
  const n = Number(current) || 0;
  return n >= 0x7fffffff ? 1 : n + 1;
};
